import type { FunnelClass } from '@onark/shared/welcomeAboardFunnelClass';
import { funnelClassMetaContentName } from '@onark/shared/welcomeAboardFunnelClass';
import Cookies from 'universal-cookie';

import { GPC_ACTIVE } from './gpc';

/**
 * Meta Pixel (fbq) helpers. Base script loads in HeadScripts on ADMIN and PUBLIC
 * (sign-up / start pages only among non-admin layouts — of GUEST courses only
 * OnArk's own funnel classes, see `funnelClassCheckInLead`), and — for the App
 * Router signup funnel, which neither HeadScripts reaches — in
 * `app/_components/MetaPixel.tsx`.
 */
declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
  interface Navigator {
    /** Global Privacy Control — https://globalprivacycontrol.org */
    globalPrivacyControl?: boolean;
  }
}

/**
 * The one OnArk pixel. Not a secret (it ships to the browser), and hard-coded
 * rather than env-driven on purpose: marketing and product must report to the
 * SAME id or a hi.onark.app → /sign-up journey splits into two visitors and the
 * retargeting audiences only ever see half a funnel.
 *
 * Duplicated in the two base snippets — `components/global/HeadScripts.tsx` and
 * `marketing/components/head-scripts.tsx`. `__tests__/meta-pixel-coverage.test.ts`
 * asserts all of them still agree with this constant, so drift fails CI.
 */
export const META_PIXEL_ID = '33095766430070000' as const;

/**
 * The production-host gate every base snippet wraps its `fbq('init')` in — a JS
 * expression string, because the snippets are inline `<script>` template
 * literals and cannot import anything.
 *
 * Why a HOSTNAME check and not an env check. Every Railway PR preview was firing
 * this pixel, because the Meta snippets are not environment-gated. On 2026-07-31
 * Meta's Events Manager Diagnostics named 25 preview domains
 * (`frontend-onark-pr-117…233.up.railway.app`) under
 * `tiered_enforcement_eu_only_full_blocking_web_actions` and gave 21 days before
 * blocking European-region data — flagged as possibly "associated with
 * individuals with specific religious or spiritual beliefs", which for church
 * software is a classification the preview traffic invited. `onark.app` itself
 * was NOT on that list; the previews built the picture.
 *
 * An env gate looked like the obvious fix and does not work here: production's
 * frontend service has no `NEXT_PUBLIC_ROLLBAR_ENV`, so client-side that falls
 * back to `NODE_ENV`, which is `'production'` for preview builds too (they are
 * production-mode Next builds). Hostname needs no build-time plumbing, cannot be
 * defeated by a missing variable, and is the same unit Meta enforces on.
 *
 * Exact-match plus a dot-prefixed suffix, never a bare `endsWith('onark.app')`,
 * which would also accept `notonark.app`.
 *
 * Also requires no Global Privacy Control signal (`navigator.globalPrivacyControl
 * === true`, via the shared `GPC_ACTIVE` check in `utils/gpc.ts`): GPC is an
 * opt-out the browser sends unprompted, and honoring it means the pixel must
 * never load — not merely skip a call — for a visitor sending it. Kept under the
 * existing (misleadingly host-only-sounding) name rather than renamed, to keep
 * this diff small; every base snippet already imports it under this name.
 */
export const META_PIXEL_HOST_GUARD =
  `(location.hostname==='onark.app'||location.hostname.endsWith('.onark.app'))&&!${GPC_ACTIVE}` as const;

const META_LEAD_CURRENCY = 'USD' as const;
const META_PURCHASE_CURRENCY = 'USD' as const;

/**
 * Standard Lead — the visitor handed over a name + church email and cleared
 * Turnstile (step 1 of 2). It fires at the point a lead actually exists, NOT
 * at the end of the trial signup: step 1 is where the volume is.
 *
 * What the ad sets OPTIMIZE on is a Meta-side setting, not this code. The
 * Aug 2026 card-on-trial campaign optimized on Lead (the paid conversion was
 * too sparse to train on); it is paused. Since the Sep 2026 matrix every
 * live ad set optimizes on StartTrial (OFFSITE_CONVERSIONS / START_TRIAL,
 * verified in Ads Manager 2026-09-07) — a trial, not an email. Lead is
 * reporting, audiences and the scoreboard's lead count; it must still be
 * exactly one event per real contact-info capture for those to mean anything.
 *
 * Exactly one Meta event per funnel stage — see `trackMetaTrialStarted` for why
 * the far end of the funnel deliberately no longer reports Lead too.
 *
 * ⚠️ This is the ONLY BROWSER `Lead` emitter in the repo and must stay that
 * way. `marketing/components/analytics.tsx` used to keep a near-copy
 * (`trackMetaLead`) whose docstring claimed to match this one but which fired
 * `Lead` on every CTA *click*. On the single shared pixel that taught the
 * LEAD-optimized campaign that a click is a conversion: Jul 2026 reported 94
 * Leads at $11.72 against 27 actual email captures. Clicks now report the
 * custom `SignupCtaClicked` below, and `__tests__/meta-pixel-coverage.test.ts`
 * greps the tree to keep this the only `fbq('track', 'Lead', …)` call site.
 * The server-side CAPI mirror is the one other place allowed to say `Lead`.
 *
 * `eventId` pairs this with the server-side Lead that `captureLead` sends (see
 * apps/api integrations/metaCapi.ts). Meta counts one Lead however many of the
 * two copies survive — and this copy is the one ad blockers and iOS webviews
 * eat. Passing it is strongly preferred; omitting it means an un-matchable
 * browser-only Lead that double-counts whenever the server copy also lands.
 *
 * `contentName` names the funnel the lead came from (signup step 1 vs the
 * Welcome Aboard checklist lead magnet) without minting a second emitter. Only
 * pass it for a real contact-info capture — an intent signal is not a Lead.
 *
 * Both are optional and both are strings, so they go in an object rather than
 * side by side positionally: `trackMetaSignupLead(someId)` reading as a content
 * name (or vice versa) is precisely the silent mislabelling this file exists to
 * prevent, and it would land on the event the lead count is read from.
 */
export function trackMetaSignupLead(
  params: { contentName?: string; eventId?: string } = {}
): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return;
  }
  window.fbq(
    'track',
    'Lead',
    {
      content_name: params.contentName ?? 'Sign up',
      currency: META_LEAD_CURRENCY,
      value: 0,
    },
    ...(params.eventId ? [{ eventID: params.eventId }] : [])
  );
}

/** `crypto.randomUUID` needs a secure context; degrade rather than throw. */
const randomId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * A fresh de-dupe id for one Lead. Minted in the BROWSER so the pixel copy and
 * the server CAPI copy can share it without a round trip; callers keep it per
 * email so a retry after a failed request re-sends the same id instead of
 * handing Meta several un-matchable Leads for one person.
 */
export const newMetaLeadEventId = (): string => `lead-${randomId()}`;

/**
 * Match keys + de-dupe id for the server-side Meta Lead the api sends
 * (apps/api integrations/metaCapi.ts `metaLeadFromRequest`).
 *
 * The pixel's `_fbp`/`_fbc` cookies have to be forwarded explicitly: the api is
 * a different origin than onark.app, so they are never sent with the GraphQL
 * POST on their own. `_fbc` is the one that carries the ad click, so dropping it
 * is the difference between a conversion Meta can credit to an ad and one it
 * can't.
 *
 * Under GPC, returns `{}` — no event id, no fbp/fbc — so the api's
 * `x-meta-event-id`-gated CAPI send (apps/api captureLead/checkIn) skips
 * entirely. Callers already just spread this into a headers object, so an
 * empty result is a no-op for them, not a shape they need to handle.
 */
export const metaLeadHeaders = (eventId: string): Record<string, string> => {
  if (
    typeof navigator !== 'undefined' &&
    navigator.globalPrivacyControl === true
  ) {
    return {};
  }

  const cookies = new Cookies();
  const fbp = cookies.get<string | undefined>('_fbp');
  const fbc = cookies.get<string | undefined>('_fbc');

  return {
    'x-meta-event-id': eventId,
    ...(fbc ? { 'x-fbc': fbc } : {}),
    ...(fbp ? { 'x-fbp': fbp } : {}),
  };
};

/**
 * The guest check-in page's Lead, for OnArk's OWN funnel classes only.
 *
 * A guest who arrives from an ad on /start/training, clicks into the free class
 * and checks in has handed over an email exactly the way the trial form's step
 * 1 does — and until 2026-09-07 only the form reported it. Meta showed one Lead
 * on a day four ad visitors gave an email; the other three chose the class.
 *
 * Same shape as the form: the browser copy fires through `trackMetaSignupLead`
 * (the one emitter) BEFORE the check-in request, so the pixel has the round
 * trip to flush before the page redirects into the class; the api sends the
 * server copy from the same request, carrying the id `headersFor` forwarded,
 * and Meta counts one Lead however many of the two survive. The content name
 * is what tells a class check-in from a form submission in Ads Manager.
 *
 * Two gates, both decided server-side and handed in as props: `funnelClass`
 * is null for every customer church's class (their guests are their people,
 * not OnArk leads, and the pixel is not even loaded on those pages), and a
 * signed-in OnArk account is not a lead. When either says no, `headersFor`
 * returns nothing and the api sends nothing either — the two halves cannot
 * disagree.
 */
export function funnelClassCheckInLead({
  funnelClass,
  signedIn,
}: {
  funnelClass: FunnelClass | null | undefined;
  signedIn: boolean;
}): {
  /** Headers for the checkIn request, or undefined when this is not a lead. */
  headersFor: (email: string) => Record<string, string> | undefined;
  /** The browser copy of the Lead. Once per email — a retry re-uses the id. */
  report: (email: string) => void;
} {
  const enabled = !!funnelClass && !signedIn;
  let minted: { email: string; id: string } | null = null;
  let reportedFor: string | null = null;
  const normalize = (email: string): string => email.trim().toLowerCase();
  const eventIdFor = (email: string): string => {
    if (minted?.email !== email) minted = { email, id: newMetaLeadEventId() };

    return minted.id;
  };

  return {
    headersFor: email =>
      enabled ? metaLeadHeaders(eventIdFor(normalize(email))) : undefined,
    report: email => {
      if (!enabled || !funnelClass) return;
      const normalized = normalize(email);

      if (reportedFor === normalized) return;
      reportedFor = normalized;
      trackMetaSignupLead({
        contentName: funnelClassMetaContentName(funnelClass),
        eventId: eventIdFor(normalized),
      });
    },
  };
}

/**
 * Custom `SignupCtaClicked` — a marketing CTA was clicked. Deliberately NOT a
 * standard event: `trackCustom` keeps it out of every standard-event audience
 * and off the LEAD-optimized campaign's conversion signal, while still being
 * available for custom conversions and retargeting if we ever want it.
 *
 * A click is intent, not a lead — nobody has handed over contact info yet, and
 * the click-to-capture ratio on /welcome-aboard was roughly 3:1.
 */
export function trackMetaSignupCtaClicked(source = 'Sign up'): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return;
  }
  window.fbq('trackCustom', 'SignupCtaClicked', {
    content_name: source,
  });
}

/**
 * Custom `GuestGapLead`, fired beside the quiz's standard `Lead` so Ads Manager
 * can tell a Guest Gap lead from a signup lead. The pixel runs in Meta's Core
 * Setup (ProtectedDataMode), which strips `content_name` and cuts the page URL
 * to the domain, so the event NAME is the only label that survives. Custom, so
 * it never becomes a second `Lead` in the optimizer's count.
 */
export function trackMetaGuestGapLead(tier: string): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return;
  }
  window.fbq('trackCustom', 'GuestGapLead', { tier });
}

/**
 * Standard InitiateCheckout — the visitor reached step 2, where a card is
 * asked for. Mid-funnel intent, between Lead and a started trial.
 *
 * `value: 0` is the literal truth of the card-on-trial offer ($0 today, first
 * charge on day 21) and the plan isn't chosen yet at this point, so there is no
 * honest price to report. Don't "fix" this by stamping the Starter price —
 * value-based bidding would then optimize toward a charge that hasn't happened
 * and may never (cancel before day 21).
 *
 * `eventId` is the Stripe SetupIntent id, which `beginTrial` also uses for the
 * server-side copy — a shared key both sides already hold, so no id has to be
 * minted or kept in sync.
 */
export function trackMetaTrialInitiateCheckout(eventId?: string): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return;
  }
  window.fbq(
    'track',
    'InitiateCheckout',
    {
      content_name: 'Free trial card step',
      currency: META_PURCHASE_CURRENCY,
      value: 0,
    },
    ...(eventId ? [{ eventID: eventId }] : [])
  );
}

/**
 * Standard StartTrial — card confirmed, church created, subscription trialing.
 *
 * This slot used to report `Lead`. Once the pixel actually loaded on /start/*
 * and /sign-up (it never did before — see `app/_components/MetaPixel.tsx`),
 * that would have made every completed signup report TWO Leads: one at step 1
 * and one here. On a LEAD-optimized campaign that both inflates the reported
 * Lead count and teaches the optimizer that step-1 leads are worth double when
 * they happen to convert. StartTrial is the correct standard event for this
 * stage and keeps the funnel one-event-per-stage.
 *
 * `value` is the annual price the trial will bill at day 21 — genuinely
 * committed to by the plan choice, unlike at the card step — so trial quality
 * is comparable across tiers. Meta treats StartTrial value as predicted, not
 * realized, revenue.
 *
 * `eventId` is the Stripe SetupIntent id, which `finalizeTrial` also uses for
 * the server-side copy — a natural shared key, so retries de-dupe for free.
 */
export function trackMetaTrialStarted(params: {
  eventId?: string;
  tier: string;
  valueDollars: number;
}): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return;
  }
  window.fbq(
    'track',
    'StartTrial',
    {
      content_name: params.tier,
      currency: META_PURCHASE_CURRENCY,
      predicted_ltv: params.valueDollars,
      value: params.valueDollars,
    },
    ...(params.eventId ? [{ eventID: params.eventId }] : [])
  );
}
